import { useState } from "react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "lucide-react";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Days per month (using non-leap year, Feb has 29 to allow for leap years)
const DAYS_IN_MONTH = {
  "01": 31, "02": 29, "03": 31, "04": 30, "05": 31, "06": 30,
  "07": 31, "08": 31, "09": 30, "10": 31, "11": 30, "12": 31
};

export default function MonthDayPicker({ value, onChange, placeholder = "Select date" }) {
  // Parse existing value (MM-DD format)
  const parseValue = (val) => {
    if (!val) return { month: "", day: "" };
    const parts = val.split("-");
    if (parts.length === 2) {
      return { month: parts[0], day: parts[1] };
    }
    // Handle old YYYY-MM-DD format - extract just MM-DD
    if (parts.length === 3) {
      return { month: parts[1], day: parts[2] };
    }
    return { month: "", day: "" };
  };

  const { month: initialMonth, day: initialDay } = parseValue(value);
  const [month, setMonth] = useState(initialMonth);
  const [day, setDay] = useState(initialDay);

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    // Reset day if it exceeds the new month's max days
    const maxDays = DAYS_IN_MONTH[newMonth] || 31;
    const currentDay = parseInt(day, 10);
    if (currentDay > maxDays) {
      setDay(String(maxDays).padStart(2, "0"));
      if (onChange) onChange(`${newMonth}-${String(maxDays).padStart(2, "0")}`);
    } else if (day) {
      if (onChange) onChange(`${newMonth}-${day}`);
    }
  };

  const handleDayChange = (newDay) => {
    setDay(newDay);
    if (month && onChange) {
      onChange(`${month}-${newDay}`);
    }
  };

  const getDaysForMonth = () => {
    const maxDays = DAYS_IN_MONTH[month] || 31;
    return Array.from({ length: maxDays }, (_, i) => {
      const dayNum = i + 1;
      return { value: String(dayNum).padStart(2, "0"), label: String(dayNum) };
    });
  };

  const formatDisplay = () => {
    if (!month || !day) return placeholder;
    const monthName = MONTHS.find(m => m.value === month)?.label || month;
    return `${monthName} ${parseInt(day, 10)}`;
  };

  const handleClear = () => {
    setMonth("");
    setDay("");
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{formatDisplay()}</span>
      </div>
      
      <div className="flex gap-2">
        <Select value={month} onValueChange={handleMonthChange}>
          <SelectTrigger className="flex-1" data-testid="month-select">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={day} onValueChange={handleDayChange} disabled={!month}>
          <SelectTrigger className="w-24" data-testid="day-select">
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent>
            {getDaysForMonth().map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(month || day) && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={handleClear}
        >
          Clear Date
        </Button>
      )}
      
      <p className="text-xs text-muted-foreground">
        Questions with this date appear first every year on this day
      </p>
    </div>
  );
}
